import { useEffect, useRef, useState } from 'react';
import { MapPin, Navigation } from 'lucide-react';

/**
 * MapsCard — inline Apple-Maps-style location card.
 *
 * Tries Apple MapKit JS first when an API key is configured at
 * `VITE_APPLE_MAPKIT_TOKEN`. Falls back to a static OpenStreetMap
 * tile via the public {s}.tile.openstreetmap.org pyramid so the
 * widget renders even without any key. Both render at 280px wide
 * to match WeatherCard / ContactCard.
 */

// `mapkit` is declared in MapPage.tsx; we just rely on it here. No
// redeclaration to avoid TS2687.

interface MapsCardProps {
  data: {
    lat?: number;
    lng?: number;
    label?: string;        // "Workshop" / "Salem, UT"
    address?: string;
    accuracy_m?: number;
    timestamp?: string;    // ISO; if older than 2h, render the stale flag
    apple_maps_url?: string;  // pre-built deep link, optional
  };
}

const MAPKIT_TOKEN: string =
  (import.meta as any).env?.VITE_APPLE_MAPKIT_TOKEN || '';

function isStale(ts?: string): boolean {
  if (!ts) return false;
  const dt = new Date(ts).getTime();
  if (!dt) return false;
  return Date.now() - dt > 2 * 3600 * 1000;
}

function osmStaticUrl(lat: number, lng: number, zoom = 14): string {
  // Renders via staticmap.openstreetmap.de — public, key-less.
  // 280×180px to match the inline-widget footprint.
  return (
    `https://staticmap.openstreetmap.de/staticmap.php?center=${lat},${lng}` +
    `&zoom=${zoom}&size=280x180&maptype=mapnik&markers=${lat},${lng},red-pushpin`
  );
}

function appleMapsDeepLink(lat: number, lng: number, label?: string): string {
  const q = label ? encodeURIComponent(label) : `${lat},${lng}`;
  return `https://maps.apple.com/?ll=${lat},${lng}&q=${q}`;
}

export default function MapsCard({ data }: MapsCardProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [usingMapKit, setUsingMapKit] = useState(false);
  const stale = isStale(data.timestamp);

  useEffect(() => {
    if (!MAPKIT_TOKEN || !data.lat || !data.lng) return;

    // Lazy-load MapKit JS once.
    const ensureMapKit = () =>
      new Promise<void>((resolve, reject) => {
        if ((window as any).mapkit) return resolve();
        const script = document.createElement('script');
        script.src = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('mapkit script failed'));
        document.head.appendChild(script);
      });

    let cancelled = false;
    (async () => {
      try {
        await ensureMapKit();
        if (cancelled || !(window as any).mapkit) return;
        (window as any).mapkit.init({
          authorizationCallback: (done: (token: string) => void) =>
            done(MAPKIT_TOKEN),
        });
        if (!containerRef.current) return;
        const map = new (window as any).mapkit.Map(containerRef.current, {
          isZoomEnabled: true,
          isScrollEnabled: false,
          showsZoomControl: false,
          showsCompass: (window as any).mapkit.FeatureVisibility.Hidden,
        });
        const coord = new (window as any).mapkit.Coordinate(
          data.lat as number,
          data.lng as number,
        );
        map.region = new (window as any).mapkit.CoordinateRegion(
          coord,
          new (window as any).mapkit.CoordinateSpan(0.01, 0.01),
        );
        const pin = new (window as any).mapkit.MarkerAnnotation(coord, {
          color: '#00d4ff',
          title: data.label || 'Here',
          subtitle: data.address || '',
        });
        map.addAnnotation(pin);
        setUsingMapKit(true);
      } catch (e) {
        // Fallback to OSM still renders — nothing to do here.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data.lat, data.lng, data.label, data.address]);

  if (data.lat == null || data.lng == null) {
    return null;
  }

  return (
    <div
      className="my-2 border border-cyan-500/30 bg-[#0a1628]/80 backdrop-blur-sm overflow-hidden max-w-[280px]"
      style={{
        clipPath:
          'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
      }}
    >
      <div className="flex items-center gap-2 px-3 pt-3 pb-2">
        <MapPin className="w-4 h-4 text-cyan-400" />
        <span className="text-cyan-300 font-semibold text-sm flex-1 truncate">
          {data.label || `${data.lat.toFixed(3)}, ${data.lng.toFixed(3)}`}
        </span>
        {stale && (
          <span className="text-[8px] tracking-widest text-amber-400 border border-amber-500/40 px-1 py-0.5">
            STALE
          </span>
        )}
      </div>

      <div ref={containerRef} className="w-full h-[180px] bg-black/40">
        {!usingMapKit && (
          <img
            src={osmStaticUrl(data.lat, data.lng)}
            alt={data.label || 'map'}
            className="w-full h-full object-cover"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        )}
      </div>

      <div className="px-3 py-2 flex items-center gap-2 text-xs">
        {data.address && (
          <span className="text-gray-300 truncate flex-1">{data.address}</span>
        )}
        <a
          href={data.apple_maps_url || appleMapsDeepLink(
            data.lat,
            data.lng,
            data.label,
          )}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1"
        >
          <Navigation className="w-3 h-3" />
          OPEN
        </a>
      </div>
    </div>
  );
}
